import React from 'react'
import {
  scrollTop,
  offset,
  height
} from 'dom-helpers/query'
import {
  on,
  off
} from 'dom-helpers/events';

var THREE = require('three');
var raycaster = new THREE.Raycaster();

const vs = `
precision lowp float;
vec4 mod289(vec4 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x)
{
  return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec2 fade(vec2 t) {
  return t*t*t*(t*(t*6.0-15.0)+10.0);
}

float cnoise(vec2 P)
{
  vec4 Pi = floor(P.xyxy) + vec4(0.0, 0.0, 1.0, 1.0);
  vec4 Pf = fract(P.xyxy) - vec4(0.0, 0.0, 1.0, 1.0);
  Pi = mod289(Pi);
  vec4 ix = Pi.xzxz;
  vec4 iy = Pi.yyww;
  vec4 fx = Pf.xzxz;
  vec4 fy = Pf.yyww;

  vec4 i = permute(permute(ix) + iy);

  vec4 gx = fract(i * (1.0 / 41.0)) * 2.0 - 1.0 ;
  vec4 gy = abs(gx) - 0.5 ;
  vec4 tx = floor(gx + 0.5);
  gx = gx - tx;

  vec2 g00 = vec2(gx.x,gy.x);
  vec2 g10 = vec2(gx.y,gy.y);
  vec2 g01 = vec2(gx.z,gy.z);
  vec2 g11 = vec2(gx.w,gy.w);

  vec4 norm = taylorInvSqrt(vec4(dot(g00, g00), dot(g01, g01), dot(g10, g10), dot(g11, g11)));
  g00 *= norm.x;
  g01 *= norm.y;
  g10 *= norm.z;
  g11 *= norm.w;

  float n00 = dot(g00, vec2(fx.x, fy.x));
  float n10 = dot(g10, vec2(fx.y, fy.y));
  float n01 = dot(g01, vec2(fx.z, fy.z));
  float n11 = dot(g11, vec2(fx.w, fy.w));

  vec2 fade_xy = fade(Pf.xy);
  vec2 n_x = mix(vec2(n00, n01), vec2(n10, n11), fade_xy.x);
  float n_xy = mix(n_x.x, n_x.y, fade_xy.y);
  return 2.3 * n_xy;
}

float map(float value, float oldMin, float oldMax, float newMin, float newMax) {
    return newMin + (newMax - newMin) * (value - oldMin) / (oldMax - oldMin);
}

varying vec3 vUv;
varying float vTime;
varying float vZ;
uniform float time;
void main()
{
    vUv = position;
    vTime = time;
    vec3 newPos = position;
    vec2 peak = vec2(1.0 - abs(.5 - uv.x), 1.0 - abs(.5 - uv.y));
    vec2 noise = vec2(
        map(cnoise(vec2(0.3 * time + uv.x * 5., uv.y * 5.)), 0., 1., -2., (peak.x * peak.y * 30.)),
        map(cnoise(vec2(-0.3 * time + uv.x * 5., uv.y * 5.)), 0., 1., -2., 25.)
    );
    newPos.z += noise.x * .06 * noise.y;
    vZ = newPos.z;
    gl_PointSize = 10.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4( newPos, 1.0 );
}
`;
const fs = `
precision lowp float;
varying vec3 vUv;
varying float vTime;
varying float vZ;
uniform sampler2D texture;
uniform vec3 fogColor;
uniform float fogNear;
uniform float fogFar;

float map(float value, float oldMin, float oldMax, float newMin, float newMax) {
    return newMin + (newMax - newMin) * (value - oldMin) / (oldMax - oldMin);
}

void main()
{
    vec3 colorA = vec3(.6, 0.17, 0.17);
    vec3 colorB = vec3(0.17, 0.8, .7);
    //vec3 color = mix(colorA, colorB, vUv.x * vUv.y);
    float alpha = map(vZ / 2., -1. / 2., 30. / 2., 0.17, 1.);
    vec3 color = vec3(.5, .5, .6);

    gl_FragColor = vec4( color, min(alpha,0.06));
    gl_FragColor = gl_FragColor * texture2D( texture, gl_PointCoord );
    #ifdef USE_FOG
         #ifdef USE_LOGDEPTHBUF_EXT
             float depth = gl_FragDepthEXT / gl_FragCoord.w;
         #else
             float depth = gl_FragCoord.z / gl_FragCoord.w;
         #endif
         float fogFactor = smoothstep( fogNear, fogFar, depth );
         gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
     #endif
}
`;


function THREERoot(props) {
  const params = {
    fov: 40,
    zNear: 1,
    zFar: 2000,
    createCameraControls: true,
    ...props
  }

  this.renderer = new THREE.WebGLRenderer({
    antialias: params.antialias,
    alpha: true
  });
  this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  this.container = params.container;
  params.container.appendChild(this.renderer.domElement);

  this.camera = new THREE.PerspectiveCamera(
    params.fov,
    window.innerWidth / 500,
    params.zNear,
    params.zfar
  );

  this.scene = new THREE.Scene();

  // this.scene.background = new THREE.Color(0x00112b);
  // this.scene.fog = new THREE.Fog(0x000000, -50, 150);

  if (params.createCameraControls) {
    this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
  }

  this.resize = this.resize.bind(this);
  this.tick = this.tick.bind(this);

  window.addEventListener('resize', this.resize, false);
}


THREERoot.prototype = {
  tick: function () {
    this.update();
    this.render();
    this.tickid = requestAnimationFrame(this.tick);
  },
  update: function () {
    this.controls && this.controls.update();
  },
  render: function () {
    this.renderer.render(this.scene, this.camera);
  },
  resize: function () {
    windowHalfX = window.innerWidth / 2;
    windowHalfY = window.innerHeight / 2;
    // var w = Math.min( 1920,window.innerWidth );
    var w = window.innerWidth;
    this.camera.aspect = w / 500;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, 500);
  },
  destroy: function () {
    requestAnimationFrame.cancel(this.tickid);
    this.tickid = null;
    window.removeEventListener('resize', this.resize, false);
    this.container.removeChild(this.renderer.domElement);
    this.renderer = null;
    this.camera = null;
    this.scene = null;
  }
};

function latLongToVector3(lat, lon, radius, heigth) {
  var phi = (lat) * Math.PI / 180;
  var theta = (lon - 180) * Math.PI / 180;

  var x = -(radius + heigth) * Math.cos(phi) * Math.cos(theta);
  var y = (radius + heigth) * Math.sin(phi);
  var z = (radius + heigth) * Math.cos(phi) * Math.sin(theta);

  return new THREE.Vector3(x, y, z);
}

function toScreenPosition(obj, camera, renderer) {
  var vector = new THREE.Vector3();

  var widthHalf = 0.5 * renderer.context.canvas.width;
  var heightHalf = 0.5 * renderer.context.canvas.height;

  obj.updateMatrixWorld();
  vector.setFromMatrixPosition(obj.matrixWorld);
  vector.project(camera);

  vector.x = (vector.x * widthHalf) + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;

  return {
    x: vector.x,
    y: vector.y
  };

};

var group,
  root;

var mouseX = 0;
var mouseXOnMouseDown = 0;

var windowHalfX = window.innerWidth / 2;
var windowHalfY = window.innerHeight / 2;

function degToRad(angle) {
  return angle * Math.PI / 180;
}

class BottomWave extends React.Component {
  time = 0
  pause = false
  createParticles() {

    const plane = new THREE.PlaneBufferGeometry(500, 250, 250, 125);

    const textureLoader = new THREE.TextureLoader();
    textureLoader.crossOrigin = '';

    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: {
          value: 1.0
        },
        texture: {
          value: textureLoader.load(require("./assets/spark.png"))
        },
        resolution: {
          value: new THREE.Vector2()
        },
        // fogColor:    { type: "c", value: root.scene.fog.color },
        // fogNear:     { type: "f", value: root.scene.fog.near },
        // fogFar:      { type: "f", value: root.scene.fog.far }
      },
      vertexShader: vs,
      fragmentShader: fs,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      transparent: true,
      // fog:true
    });

    this.particles = new THREE.Points(plane, material);
    this.particles.rotation.x = degToRad(-90);

    root.scene.add(this.particles);
  }

  onScroll = e => {
    const offsetTop = offset(this.container).top;
    const scrolly = scrollTop(document);
    const mHeight = height(this.container)

    if (scrolly + window.innerHeight > offsetTop && offsetTop + mHeight > scrolly) {
      // display
      if (this.pause) {
        this.pause = false;
        this.update()
      }

    } else {
      this.pause = true;
    }

  }
  componentWillUnmount() {
    off(document, 'scroll', this.onScroll);
  }

  componentDidMount() {
    var canvas = document.createElement('canvas');
    var gl;
    var experimental = false;

    try {
      gl = canvas.getContext("webgl");
    } catch (x) {
      gl = null;
    }

    if (gl == null) {
      try {
        gl = canvas.getContext("experimental-webgl");
        experimental = true;
      } catch (x) {
        gl = null;
      }
    }

    if (gl) {
      root = new THREERoot({
        createCameraControls: false,
        antialias: 1,
        fov: 60,
        container: this.container
      });
      root.camera.position.set(0, 50, -70)
      root.camera.lookAt(new THREE.Vector3(0, 0, 0));
      root.renderer.setClearColor(0, 0);
      root.renderer.setPixelRatio(window.devicePixelRatio || 1);

      this.createParticles()
      this.update()
      root.resize()
    }
    on(document, 'scroll', this.onScroll);
    this.onScroll()
  }

  update = () => {
    if (this.pause) return
    this.particles.material.uniforms.time.value = this.time;
    this.time += 0.01
    root.render()
    requestAnimationFrame(this.update);
  }


  render() {
    return <div className = "waves"
    ref = {
      container => this.container = container
    }
    />
  }
}

export default BottomWave;



// WEBPACK FOOTER //
// ./src/BottomWave.js